/**
 * Checklist Reveal — animierte Häkchen-Liste.
 *
 * Saubere Vorteils-/Anforderungs-Liste: jede Zeile ist eine Glas-Pille mit
 * einem Häkchen-Badge, dessen Haken sich live „zeichnet" (Stroke-Draw), die
 * Pille blitzt kurz im Akzent auf, der Text gleitet ein. Ideal für „Das
 * bekommst du", „Alles inklusive", Anforderungen abhaken.
 *
 * Items-Format:  "Haupttext | optionaler Zusatz"
 *   z. B. "0 % Provision | Du behältst jeden Cent"
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, gsapSetLine,
  makeTl, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";
import { splitItem } from "../visual/_mockHelpers.js";

export const checklistReveal: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const isVertical = ctx.format === "9:16";

  const eyebrow = inputs.extras?.eyebrow != null ? String(inputs.extras.eyebrow) : "";
  const headline = inputs.text ?? "";

  const items = (inputs.items?.length
    ? inputs.items
    : [
        "0 % Provision | Du behältst jeden Cent",
        "Verifizierte Anbieter | Echt, lokal, geprüft",
        "Sicher bezahlen | Stripe & Käuferschutz",
        "Direkt & ohne Umwege | Kontakt in einem Tap",
      ]
  ).slice(0, isVertical ? 6 : 5);

  const lg = typeSize("lg", ctx.format);
  const md = typeSize("md", ctx.format);
  const sm = typeSize("sm", ctx.format);

  const wrapId = `${clipPrefix}-wrap`;
  const eyeId = `${clipPrefix}-eye`;
  const headId = `${clipPrefix}-head`;

  const parsed = items.map((raw) => {
    const [main, sub] = splitItem(raw);
    return { main: (main ?? "").trim(), sub: (sub ?? "").trim() };
  });

  const box = isVertical ? 70 : 64;
  const mainPx = isVertical ? md * 0.66 : md * 0.58;
  const subPx = isVertical ? sm * 0.6 : sm * 0.54;

  const rowIds = parsed.map((_, i) => `${clipPrefix}-row-${i}`);
  const checkIds = parsed.map((_, i) => `${clipPrefix}-chk-${i}`);
  const boxIds = parsed.map((_, i) => `${clipPrefix}-box-${i}`);

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "allyver", accent });

  const checkLen = 34; // großzügig > Pfadlänge des Hakens (kein sichtbarer Rest)
  function renderRow(i: number, it: (typeof parsed)[number]): string {
    return `<div id="${rowIds[i]}" style="display:flex;align-items:center;gap:${isVertical ? 24 : 26}px;padding:${isVertical ? "18px 26px" : "16px 28px"};border-radius:9999px;background:linear-gradient(120deg, ${withAlpha(themeColors.fg, 0.06)} 0%, ${withAlpha(themeColors.bg, 0.55)} 100%);border:1px solid ${withAlpha(accent, 0.28)};box-shadow:0 14px 34px rgba(0,0,0,0.28);will-change:transform,opacity;">
      <div id="${boxIds[i]}" style="flex-shrink:0;width:${box}px;height:${box}px;border-radius:18px;display:flex;align-items:center;justify-content:center;background:radial-gradient(circle at 32% 28%, ${withAlpha(accent, 0.34)} 0%, ${withAlpha(accent, 0.1)} 70%, ${withAlpha(themeColors.bg, 0.7)} 100%);border:1px solid ${withAlpha(accent, 0.55)};box-shadow:0 0 22px ${withAlpha(accent, 0.4)};">
        <svg width="${Math.round(box * 0.56)}" height="${Math.round(box * 0.56)}" viewBox="0 0 24 24" fill="none" stroke="${accent}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path id="${checkIds[i]}" d="M20 6 9 17l-5-5" /></svg>
      </div>
      <div style="display:flex;flex-direction:column;gap:3px;min-width:0;">
        <div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${mainPx}px;line-height:1.06;letter-spacing:-0.8px;color:${themeColors.fg};text-wrap:balance;">${escapeHtml(it.main)}</div>
        ${it.sub ? `<div style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${subPx}px;letter-spacing:0.1px;line-height:1.28;color:${withAlpha(themeColors.fg, 0.66)};">${escapeHtml(it.sub)}</div>` : ""}
      </div>
    </div>`;
  }

  const rowsHtml = parsed.map((it, i) => renderRow(i, it)).join("");
  const eyePx = sm * 0.6;
  const headPx = isVertical ? lg * 0.84 : lg * 0.76;
  const hasHeader = Boolean(headline || eyebrow);

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:stretch;justify-content:center;gap:${isVertical ? 28 : 26}px;padding:${isVertical ? "120px 92px" : "110px 150px"};overflow:hidden;">
    ${hasHeader ? `<div style="display:flex;flex-direction:column;gap:12px;align-items:center;text-align:center;max-width:${isVertical ? 940 : 1300}px;margin:0 auto;">
      ${eyebrow ? `<div id="${eyeId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyePx}px;letter-spacing:6px;text-transform:uppercase;color:${accent};display:inline-flex;align-items:center;gap:14px;"><span style="width:34px;height:2px;background:${accent};"></span><span>${escapeHtml(eyebrow)}</span></div>` : ""}
      ${headline ? `<div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headPx}px;line-height:1.04;letter-spacing:-1.8px;color:${themeColors.fg};text-wrap:balance;">${escapeHtml(headline)}</div>` : ""}
    </div>` : ""}
    <div style="display:flex;flex-direction:column;gap:${isVertical ? 16 : 14}px;max-width:${isVertical ? 980 : 1180}px;width:100%;margin:0 auto;">${rowsHtml}</div>
  </div>`;

  const init = [
    eyebrow ? gsapSetLine(`#${eyeId}`, { opacity: 0, y: -8 }) : "",
    headline ? gsapSetLine(`#${headId}`, { opacity: 0, y: 22 }) : "",
    ...rowIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, x: -34 })),
    ...checkIds.map((id) => gsapSetLine(`#${id}`, { strokeDasharray: checkLen, strokeDashoffset: checkLen })),
    ...boxIds.map((id) => gsapSetLine(`#${id}`, { scale: 0.8 })),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [];
  if (eyebrow) tweens.push(t.to(`#${eyeId}`, { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" }, 0.1));
  if (headline) tweens.push(t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "power3.out" }, 0.2));

  const start = hasHeader ? 0.5 : 0.22;
  const revealWindow = Math.min(2.2, Math.max(0.9, clipDurationSec * 0.55));
  const step = revealWindow / Math.max(parsed.length, 1);

  parsed.forEach((_, i) => {
    const at = start + i * step;
    tweens.push(t.to(`#${rowIds[i]}`, { opacity: 1, x: 0, duration: 0.45, ease: "power3.out" }, at));
    tweens.push(t.to(`#${boxIds[i]}`, { scale: 1, duration: 0.34, ease: "back.out(2.4)" }, at + 0.05));
    tweens.push(t.to(`#${checkIds[i]}`, { strokeDashoffset: 0, duration: 0.34, ease: "power2.out" }, at + 0.14));
  });

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
